import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';
import reactUpdate from 'immutability-helper';
import React from 'react';
import _ from 'underscore';

import * as OrganizationActions from 'actions/OrganizationActions';
import getPolicyName from 'auth/getPolicyName';
import getUserFacingPolicies from 'auth/getUserFacingPolicies';
import {getDefaultDataPolicy} from 'auth/IntrinsicPolicies';
import * as SourceConstants from 'constants/SourceConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import NavStore from 'nav/NavStore';
import notifications from 'notifications';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {Header1} from 'ui-library/Headers';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import Menu from 'ui-library/menus/Menu';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import * as nyc from 'ui-library/NYC';
import ScrollableTable from 'ui-library/scrollable/ScrollableTable';
import {RowSelectionMode, ScrollableTableRow} from 'ui-library/scrollable/ScrollableTableConstants';
import {readableTime} from 'util/DateUtil';

import {ShareProjectsPageQuery} from './__generated/ShareProjectsPageQuery';

type ShareProjectsPageQueryDataPolicy = ShareProjectsPageQuery['currentTenant']['dataPolicies'][number];

const {PROJECT_TYPE} = SourceConstants;

export const SHARE_PROJECTS_PAGE_QUERY: DocumentNode = gql`
  query ShareProjectsPageQuery @skipAudit @engComponent(name: IDENTITY) {
    currentTenant {
      id
      dataPolicies {
        id
        name
        isDefault
        isHidden
      }
    }
  }
`;

interface PermissionsCellProps {
  dataPolicies: Array<ShareProjectsPageQueryDataPolicy>;
  selectedDataPolicyId: string;
  onSelectDataPolicy: (dataPolicyId: string) => void;
}

function PermissionsCell({
  selectedDataPolicyId,
  onSelectDataPolicy,
  dataPolicies,
}: PermissionsCellProps): JSX.Element {
  const selectedDataPolicy = dataPolicies.find(({id}) => id === selectedDataPolicyId)!;
  return (
    <SelectDropdown
      displayName={getPolicyName(selectedDataPolicy)}
      menu={
        <Menu width={MenuWidth.FULL}>
          {dataPolicies.map((dataPolicy) => (
            <Option
              key={dataPolicy.id}
              onSelect={() => {
                onSelectDataPolicy(dataPolicy.id);
              }}
              value={dataPolicy.id}
            >
              {getPolicyName(dataPolicy)}
            </Option>
          ))}
        </Menu>
      }
    />
  );
}

export interface ShareProjectsPageProps {
  isLoading: boolean;
  organizationId: string;
  userFacingDataPolicies: Array<ShareProjectsPageQueryDataPolicy>;
}

interface State {
  projects: Array<SourceConstants.NavStoreProject>;
  projectToPolicyIds: {[projectId: string]: string};
  projectToSelected: {[projectId: string]: boolean};
}

class ShareProjectsPage extends React.Component<ShareProjectsPageProps, State> {
  getInitialState(): State {
    const projects = NavStore.getSources()!.filter(
      ({sourceType, permissions}) => permissions.owner && sourceType === PROJECT_TYPE
    ) as Array<SourceConstants.NavStoreProject>;
    const defaultPolicy = getDefaultDataPolicy(this.props.userFacingDataPolicies);
    const projectToPolicyIds = _.object(projects.map((project) => [project.id, defaultPolicy.id]));
    const projectToSelected = _.object(projects.map((project) => [project.id, false]));
    return {
      projects,
      projectToPolicyIds,
      projectToSelected,
    };
  }

  override state: State = this.getInitialState();

  _getRowContentHeight = (): number => 28; // Height of select dropdown

  _handleShareProjects = (): void => {
    // eslint-disable-next-line no-restricted-syntax
    const selectedProjects = _.filter(
      this.state.projects,
      (project) => this.state.projectToSelected[project.id]
    );
    // eslint-disable-next-line no-restricted-syntax
    const sourceIds = _.map(selectedProjects, (project) => project.id);
    // eslint-disable-next-line no-restricted-syntax
    const dataPolicyids = _.map(selectedProjects, (project) => this.state.projectToPolicyIds[project.id]);
    OrganizationActions.shareProjectsWithOrganization(sourceIds, dataPolicyids, this.props.organizationId);
  };

  _handleToggleSelectProjects = (projects: Array<ScrollableTableRow>, isSelected: boolean): void => {
    let projectToSelected = this.state.projectToSelected;
    projects.forEach((project) => {
      projectToSelected = Object.assign(projectToSelected, {
        [project.id]: isSelected,
      });
    });
    this.setState({projectToSelected});
  };

  override render(): JSX.Element {
    const columns = [
      {
        header: 'Project Name',
        accessorKey: 'name',
        flexGrow: 1,
      },
      {
        header: 'Created',
        accessorKey: 'createdAt',
        flexGrow: 1,
      },
      {
        header: 'Permissions',
        accessorKey: 'permissions',
        flexGrow: 1,
      },
    ];

    // eslint-disable-next-line no-restricted-syntax
    const rows = _.map(this.state.projects, (project) => ({
      id: project.id,
      data: {
        name: project.name,
        createdAt: readableTime(project.createdAt),
        permissions: (
          <PermissionsCell
            dataPolicies={this.props.userFacingDataPolicies}
            selectedDataPolicyId={this.state.projectToPolicyIds[project.id]}
            onSelectDataPolicy={(dataPolicyId) => {
              this.setState({
                projectToPolicyIds: reactUpdate(this.state.projectToPolicyIds, {
                  $merge: {[project.id]: dataPolicyId},
                }),
                projectToSelected: reactUpdate(this.state.projectToSelected, {
                  $merge: {[project.id]: true},
                }),
              });
            }}
          />
        ),
      },
      isSelected: this.state.projectToSelected[project.id],
    }));

    return (
      <nyc.Container size="lg">
        <nyc.Row>
          <nyc.Col lg={1} />
          <nyc.Col lg={20}>
            <div className="shareProjectsPage-title u-tiny-spacing-bottom">
              Choose projects to share with your organization
            </div>
          </nyc.Col>
        </nyc.Row>
        <nyc.Row className="u-spacing-bottom">
          <nyc.Col lg={1} />
          <nyc.Col lg={20}>
            <div className="shareProjectsPage-subtitle">
              <Header1>Build a library of shared resources to increase collaboration and visibility</Header1>
            </div>
            <ScrollableTable
              rowSelectionMode={RowSelectionMode.MULTIPLE}
              columns={columns}
              onRowsSelect={this._handleToggleSelectProjects}
              getRowContentHeight={this._getRowContentHeight}
              rows={rows}
            />
            <Button
              buttonStyle={ButtonStyle.PRIMARY}
              onClick={this._handleShareProjects}
              disabled={this.props.isLoading}
              className="pull-right u-small-spacing-top"
              size={ButtonSize.MEDIUM}
              text={this.props.isLoading ? 'Completing Setup...' : 'Share and Complete Setup'}
              dataTestAttrs={{component: 'ShareProjectsPage', element: 'complete'}}
            />
          </nyc.Col>
        </nyc.Row>
      </nyc.Container>
    );
  }
}

export interface QueryingShareProjectsPageProps {
  organizationId: string;
  isLoading: boolean;
}

export default function QueryingShareProjectsPage(props: QueryingShareProjectsPageProps): JSX.Element | null {
  const result = useBenchlingQuery<ShareProjectsPageQuery>(SHARE_PROJECTS_PAGE_QUERY);
  if (result.type === QueryResultType.ERROR) {
    notifications.error('Could not load tenant. Try reloading the page.');
    return null;
  } else if (result.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  } else {
    const dataPolicies = result.data.currentTenant.dataPolicies;
    const userFacingDataPolicies = getUserFacingPolicies(dataPolicies);
    return (
      <ShareProjectsPage
        userFacingDataPolicies={userFacingDataPolicies}
        organizationId={props.organizationId}
        isLoading={props.isLoading}
      />
    );
  }
}
